import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { renderGoogleButton, handleGoogleRedirectCallback } from '../utils/googleAuth';
import BrandLoadingScreen from '../components/BrandLoadingScreen';
import SEOHead from '../components/SEOHead';

const NIGERIAN_PHONE_REGEX = /^(\+?234|0)(70|80|81|90|91)\d{8}$/;
const NIGERIAN_PREFIXES = [
  '0803','0806','0703','0706','0813','0816','0810','0814','0903','0906','0913','0916',
  '0802','0808','0708','0812','0701','0902','0901','0907','0912',
  '0805','0807','0705','0815','0811','0905','0915',
  '0809','0818','0817','0909','0908',
];
const DISPOSABLE_DOMAINS = [
  'mailinator.com','guerrillamail.com','tempmail.com','throwaway.email',
  'yopmail.com','fakeinbox.com','trashmail.com','sharklasers.com',
  'temp-mail.org','dispostable.com','maildrop.cc','10minutemail.com',
];

function validateEmail(email) {
  const t = email.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(t)) return 'Enter a valid email address';
  const domain = t.split('@')[1];
  if (DISPOSABLE_DOMAINS.includes(domain)) return 'Disposable emails are not allowed';
  return null;
}
function validatePhone(phone) {
  const c = phone.replace(/[\s\-()]/g, '');
  if (!c) return 'Phone number is required';
  if (!NIGERIAN_PHONE_REGEX.test(c)) return 'Enter a valid Nigerian phone number (e.g. 08012345678)';
  let local = c.startsWith('+234') ? '0' + c.slice(4) : c.startsWith('234') ? '0' + c.slice(3) : c;
  const prefix = local.slice(0, 4);
  if (!NIGERIAN_PREFIXES.includes(prefix)) return 'Enter a valid Nigerian mobile number';
  return null;
}
function validatePassword(pwd) {
  return { length: pwd.length >= 8, uppercase: /[A-Z]/.test(pwd), number: /[0-9]/.test(pwd), special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd) };
}

// Shared styles
const inputCls = 'w-full px-5 py-4 text-lg rounded-2xl border-2 border-[#d9e5ef] bg-white focus:outline-none focus:border-[#294c79] focus:ring-4 focus:ring-[#294c79]/10 transition placeholder-[#1a1a1a]/30';
const labelCls = 'block text-2xl sm:text-3xl font-extrabold text-[#1a1a2e] mb-1';
const hintCls = 'text-sm text-[#1a1a1a]/50 mb-5';
const btnCls = 'mt-5 px-8 py-3.5 rounded-2xl bg-[#294c79] text-white font-semibold text-base hover:bg-[#1f3a5c] active:scale-95 transition-all shadow-md disabled:opacity-40 disabled:cursor-not-allowed';
const errCls = 'mt-2 text-sm text-red-600 font-medium';

function ProgressBar({ current, total }) {
  return (
    <div className="w-full bg-[#e8f2d1] rounded-full h-1.5 mb-8">
      <div className="h-1.5 rounded-full bg-[#294c79] transition-all duration-500" style={{ width: `${Math.round((current / total) * 100)}%` }} />
    </div>
  );
}

function Slide({ visible, children }) {
  return (
    <div className={`transition-all duration-500 ease-in-out ${visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-6 pointer-events-none absolute inset-0'}`}>
      {children}
    </div>
  );
}

export default function SignupPage() {
  const [slide, setSlide] = useState(0);
  const [form, setForm] = useState({ fullName: '', phone: '', email: '', password: '', confirmPassword: '' });
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);
  const googleBtnRef = useRef(null);
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  useEffect(() => {
    const result = handleGoogleRedirectCallback();
    if (result) {
      axios.post(`${process.env.REACT_APP_API_URL}/api/auth/google`, { token: result.credential })
        .then(res => { if (res.data.status === 'success') { login(res.data.user, res.data.token); navigate(result.returnPath || '/dashboard', { replace: true }); } else { setApiError(res.data.message || 'Google Sign-In failed.'); } })
        .catch(() => setApiError('Google Sign-In failed.'));
      return;
    }
    if (googleBtnRef.current) {
      renderGoogleButton(process.env.REACT_APP_GOOGLE_CLIENT_ID, googleBtnRef.current,
        async (credential) => {
          setApiError('');
          try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/google`, { token: credential });
            if (res.data.status === 'success') { login(res.data.user, res.data.token); navigate('/dashboard', { replace: true }); }
            else setApiError(res.data.message || 'Google Sign-In failed.');
          } catch (err) { setApiError(err.response?.data?.error || 'Google Sign-In failed.'); }
        }, (msg) => { if (msg) setApiError(msg); }
      );
    }
  }, []);

  useEffect(() => { setTimeout(() => inputRef.current?.focus?.(), 520); }, [slide]);

  const set = (field, value) => { setForm(prev => ({ ...prev, [field]: value })); setErrors(prev => ({ ...prev, [field]: '' })); setApiError(''); };
  const next = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); setSlide(s => s + 1); };
  const back = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); setSlide(s => s - 1); };

  const pwdRules = validatePassword(form.password);
  const allPwdMet = Object.values(pwdRules).every(Boolean);
  const pwdsMatch = form.password === form.confirmPassword && form.confirmPassword !== '';

  const validateName = () => {
    const t = form.fullName.trim();
    if (!t) return 'Full name is required';
    if (t.split(' ').length < 2) return 'Please enter both first and last name';
    if (/\d/.test(t)) return 'Name should not contain numbers';
    return null;
  };

  const handleSignup = async () => {
    setApiError(''); setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/signup`, {
        fullName: form.fullName.trim(), phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(), password: form.password,
      });
      if (res.data.status === 'success') { next(); } // move to OTP slide
      else setApiError(res.data.message || 'Signup failed');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || '';
      if (msg.toLowerCase().includes('phone')) setErrors(prev => ({ ...prev, phone: 'This phone is already registered.' }));
      else if (msg.toLowerCase().includes('email')) setErrors(prev => ({ ...prev, email: 'This email is already registered.' }));
      else setApiError(msg || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) { setApiError('Enter the 6-digit code'); return; }
    setApiError(''); setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/verify-otp`, { email: form.email.trim().toLowerCase(), otp });
      if (res.data.status === 'success') {
        localStorage.setItem('authToken', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user || {}));
        setDone(true);
        setTimeout(() => navigate('/login'), 2500);
      } else setApiError(res.data.message || 'Invalid or expired code');
    } catch (err) { setApiError(err.response?.data?.message || err.response?.data?.error || 'Verification failed'); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setApiError(''); setLoading(true);
    try { await axios.post(`${process.env.REACT_APP_API_URL}/api/resend-otp`, { email: form.email.trim().toLowerCase() }); setOtp(''); }
    catch (err) { setApiError(err.response?.data?.error || 'Could not resend code.'); }
    finally { setLoading(false); }
  };

  // OTP slide index = 5 (after: 0=welcome, 1=name, 2=phone, 3=email, 4=password, 5=otp)
  const TOTAL = 4;

  if (done) return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fbff] via-white to-[#f5f9ec] flex items-center justify-center px-5">
      <div className="text-center">
        <div className="w-24 h-24 rounded-full bg-[#e8f2d1] flex items-center justify-center mx-auto mb-6 text-5xl">✅</div>
        <h1 className="text-3xl font-extrabold text-[#294c79] mb-3">Account created!</h1>
        <p className="text-[#1a1a1a]/60">Taking you to sign in…</p>
      </div>
    </div>
  );

  return (
    <>
      <SEOHead title="Create Account | LiteRent" description="Create your LiteRent account and start renting smarter." canonical="https://literent.com.ng/signup" />

      <div className="min-h-screen bg-gradient-to-br from-[#f8fbff] via-white to-[#f5f9ec] flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between px-5 sm:px-10 py-4 border-b border-[#edf2f7] bg-white/80 backdrop-blur-sm sticky top-0 z-10">
          <Link to="/" className="flex items-center gap-2 text-[#294c79] font-semibold text-sm hover:opacity-75 transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Home
          </Link>
          <div className="text-sm font-medium text-[#294c79]/60">
            {slide > 0 && slide <= TOTAL ? `${slide} of ${TOTAL}` : ''}
          </div>
          <img src="/Logo.jpg" alt="LiteRent" className="h-8 w-auto rounded-lg" />
        </header>

        <main className="flex-1 flex items-center justify-center px-5 py-12 sm:px-8">
          <div className="w-full max-w-lg relative">

            {slide > 0 && slide <= TOTAL && <ProgressBar current={slide} total={TOTAL} />}

            {/* ── SLIDE 0: Welcome ── */}
            <Slide visible={slide === 0}>
              <div className="text-center mb-10">
                <Link to="/" className="inline-block mb-6">
                  <div className="inline-flex items-center justify-center rounded-[1.5rem] bg-white p-3 shadow-lg border border-white/30">
                    <img src="/Logo.jpg" alt="LiteRent" className="h-20 w-auto rounded-[1.2rem] object-contain" />
                  </div>
                </Link>
                <h1 className="text-4xl sm:text-5xl font-extrabold text-[#1a1a2e] leading-tight mb-4">
                  Start your<br /><span className="text-[#294c79]">LiteRent</span> journey
                </h1>
                <p className="text-[#1a1a1a]/60 text-lg mb-8">Rent quality homes with just 30% upfront. Takes about a minute to sign up.</p>
              </div>
              {apiError && <div className="mb-4 p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{apiError}</div>}
              <div ref={googleBtnRef} className="mb-5 flex justify-center min-h-[44px]" />
              <div className="flex items-center gap-4 mb-5">
                <div className="flex-1 h-px bg-[#e5e7eb]" /><span className="text-sm text-[#1a1a1a]/40 font-medium">or continue with email</span><div className="flex-1 h-px bg-[#e5e7eb]" />
              </div>
              <button onClick={next} className="w-full py-4 rounded-2xl bg-[#294c79] text-white font-bold text-lg hover:bg-[#1f3a5c] active:scale-[0.98] transition-all shadow-md">
                Sign up with email →
              </button>
              <p className="text-center mt-5 text-sm text-[#1a1a1a]/55">Already have an account? <Link to="/login" className="text-[#294c79] font-semibold underline">Sign in</Link></p>
            </Slide>

            {/* ── SLIDE 1: Full Name ── */}
            <Slide visible={slide === 1}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">Step 1 of 4</p>
              <label className={labelCls}>What's your full name?</label>
              <p className={hintCls}>First and last name, as it appears on your ID.</p>
              <input
                ref={inputRef}
                type="text"
                className={inputCls + (errors.fullName ? ' border-red-400' : '')}
                placeholder="Victor Prince"
                value={form.fullName}
                onChange={e => set('fullName', e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const err = validateName();
                    if (err) { setErrors(p => ({ ...p, fullName: err })); return; }
                    next();
                  }
                }}
              />
              {errors.fullName && <p className={errCls}>{errors.fullName}</p>}
              <button
                onClick={() => {
                  const err = validateName();
                  if (err) { setErrors(p => ({ ...p, fullName: err })); return; }
                  next();
                }}
                disabled={!form.fullName.trim()}
                className={btnCls}
              >OK, next →</button>
            </Slide>

            {/* ── SLIDE 2: Phone ── */}
            <Slide visible={slide === 2}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">Step 2 of 4</p>
              <label className={labelCls}>What's your phone number?</label>
              <p className={hintCls}>A Nigerian mobile number — we'll use this for important updates.</p>
              <input
                ref={inputRef}
                type="tel"
                className={inputCls + (errors.phone ? ' border-red-400' : '')}
                placeholder="08012345678 or +2348012345678"
                value={form.phone}
                onChange={e => set('phone', e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const err = validatePhone(form.phone.trim());
                    if (err) { setErrors(p => ({ ...p, phone: err })); return; }
                    next();
                  }
                }}
              />
              {errors.phone ? <p className={errCls}>{errors.phone}</p> : <p className="mt-2 text-xs text-[#1a1a1a]/40">MTN, Airtel, Glo or 9mobile numbers accepted.</p>}
              <button
                onClick={() => {
                  const err = validatePhone(form.phone.trim());
                  if (err) { setErrors(p => ({ ...p, phone: err })); return; }
                  next();
                }}
                disabled={!form.phone.trim()}
                className={btnCls}
              >OK, next →</button>
              <button onClick={back} className="mt-3 px-6 py-3 rounded-2xl border border-[#d9e5ef] text-[#294c79] font-medium text-sm hover:bg-[#f3f7fb] transition ml-3">← Back</button>
            </Slide>

            {/* ── SLIDE 3: Email ── */}
            <Slide visible={slide === 3}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">Step 3 of 4</p>
              <label className={labelCls}>What's your email address?</label>
              <p className={hintCls}>We'll send your verification code here.</p>
              <input
                ref={inputRef}
                type="email"
                className={inputCls + (errors.email ? ' border-red-400' : '')}
                placeholder="you@example.com"
                value={form.email}
                onChange={e => set('email', e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const err = validateEmail(form.email);
                    if (err) { setErrors(p => ({ ...p, email: err })); return; }
                    next();
                  }
                }}
              />
              {errors.email && <p className={errCls}>{errors.email}</p>}
              <button
                onClick={() => {
                  const err = validateEmail(form.email);
                  if (err) { setErrors(p => ({ ...p, email: err })); return; }
                  next();
                }}
                disabled={!form.email.trim()}
                className={btnCls}
              >OK, next →</button>
              <button onClick={back} className="mt-3 px-6 py-3 rounded-2xl border border-[#d9e5ef] text-[#294c79] font-medium text-sm hover:bg-[#f3f7fb] transition ml-3">← Back</button>
            </Slide>

            {/* ── SLIDE 4: Password ── */}
            <Slide visible={slide === 4}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">Step 4 of 4</p>
              <label className={labelCls}>Create a password</label>
              <p className={hintCls}>Make it strong — you'll use this to sign in.</p>
              <div className="space-y-3 mb-4">
                <div className="relative">
                  <input
                    ref={inputRef}
                    type={showPwd ? 'text' : 'password'}
                    className={inputCls + ' pr-16'}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={e => set('password', e.target.value)}
                  />
                  <button type="button" tabIndex={-1} onClick={() => setShowPwd(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#294c79] font-semibold">
                    {showPwd ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    className={inputCls + ' pr-16' + (form.confirmPassword && !pwdsMatch ? ' border-red-400' : form.confirmPassword && pwdsMatch ? ' border-green-400' : '')}
                    placeholder="Confirm password"
                    value={form.confirmPassword}
                    onChange={e => set('confirmPassword', e.target.value)}
                  />
                  <button type="button" tabIndex={-1} onClick={() => setShowConfirm(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#294c79] font-semibold">
                    {showConfirm ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              {form.password && (
                <div className="rounded-2xl border border-[#edf2f7] bg-[#fbfcfe] px-4 py-4 mb-4 text-sm space-y-1.5">
                  {[['At least 8 characters', pwdRules.length], ['One uppercase letter', pwdRules.uppercase], ['One number', pwdRules.number], ['One special character', pwdRules.special]].map(([label, ok]) => (
                    <div key={label} className={`flex items-center gap-2 ${ok ? 'text-green-600' : 'text-[#1a1a1a]/40'}`}>
                      <span className="text-base">{ok ? '✓' : '○'}</span><span>{label}</span>
                    </div>
                  ))}
                  {form.confirmPassword && <div className={`flex items-center gap-2 ${pwdsMatch ? 'text-green-600' : 'text-red-500'}`}><span>{pwdsMatch ? '✓' : '✕'}</span><span>Passwords match</span></div>}
                </div>
              )}
              <div className="rounded-2xl border border-[#edf2f7] bg-[#fbfcfe] px-4 py-4 mb-4">
                <label className="flex items-start gap-3 text-sm text-[#1a1a1a]/70 cursor-pointer">
                  <input type="checkbox" checked={!!form.acceptTerms} onChange={e => set('acceptTerms', e.target.checked)} className="mt-1 w-5 h-5 rounded text-[#a5c250]" />
                  <span>I accept the <Link to="/terms" className="text-[#294c79] underline">Terms of Service</Link> and <Link to="/privacy" className="text-[#294c79] underline">Privacy Policy</Link>.</span>
                </label>
              </div>
              {apiError && <div className="mb-3 p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{apiError}</div>}
              <div className="flex gap-3">
                <button
                  onClick={handleSignup}
                  disabled={loading || !allPwdMet || !pwdsMatch || !form.acceptTerms}
                  className={btnCls}
                >
                  {loading ? 'Creating account…' : 'Create My Account →'}
                </button>
                <button onClick={back} className="mt-5 px-6 py-3 rounded-2xl border border-[#d9e5ef] text-[#294c79] font-medium text-sm hover:bg-[#f3f7fb] transition">← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 5: OTP ── */}
            <Slide visible={slide === 5}>
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-[#e8f2d1] flex items-center justify-center mx-auto mb-6">
                  <svg className="w-9 h-9 text-[#4a7a1e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-extrabold text-[#1a1a2e] mb-2">Check your email</h2>
                <p className="text-[#1a1a1a]/55 mb-1">We sent a 6-digit code to</p>
                <p className="font-bold text-[#294c79] text-lg mb-7">{form.email}</p>
              </div>
              <input
                ref={inputRef}
                value={otp}
                onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setApiError(''); }}
                maxLength={6}
                placeholder="——————"
                inputMode="numeric"
                className="w-full text-center text-5xl font-mono tracking-[0.5em] py-5 rounded-2xl border-2 border-[#d9e5ef] focus:outline-none focus:border-[#294c79] focus:ring-4 focus:ring-[#294c79]/10 bg-white transition"
              />
              {otp && <p className={`mt-2 text-center text-sm font-medium ${otp.length === 6 ? 'text-[#4a7a1e]' : 'text-[#1a1a1a]/40'}`}>{otp.length}/6 digits</p>}
              {apiError && <div className="mt-3 p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{apiError}</div>}
              <button
                onClick={handleVerify}
                disabled={loading || otp.length !== 6}
                className={btnCls + ' w-full'}
              >
                {loading ? 'Verifying…' : 'Verify & Continue →'}
              </button>
              <div className="mt-4 text-center text-sm text-[#1a1a1a]/55 space-y-2">
                <p>Didn't get it? Check spam or <button type="button" onClick={handleResend} disabled={loading} className="text-[#294c79] font-semibold underline disabled:opacity-50">resend code</button></p>
                <button type="button" onClick={back} className="text-[#294c79] hover:underline text-xs">← Use a different email</button>
              </div>
            </Slide>

          </div>
        </main>

        <footer className="py-5 text-center text-[#1a1a1a]/40 text-xs border-t border-[#edf2f7]">
          © {new Date().getFullYear()} LiteRent · All rights reserved
        </footer>
      </div>
    </>
  );
}
